import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addMapConnection,
  addMapSystem,
  createMap,
  deleteMap,
  fetchMapConnections,
  fetchTrackedConnections,
  fetchMaps,
  fetchMapSystems,
  removeMapSystem,
  renameCompartment,
  updateMapSystem,
} from '../../api/maps';
import {
  addFavoriteSystem,
  fetchFavoriteSystems,
  removeFavoriteSystem,
  searchSystems,
  SystemSearchResult,
} from '../../api/systems';
import LockIcon from '@mui/icons-material/Lock';
import AskDialog from '../../components/AskDialog';
import Chevron from '../../components/Chevron';
import ScanFrame from '../../components/ScanFrame';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../services/AuthContext';
import { C, FONT, frameSx, hexToRgba, revealSx } from '../../theme/tokens';
import { MapSystem, ShareScope, TrackedConnection } from '../../types';
import CompartmentList, { CompartmentHandlers } from './CompartmentList';
import ConnectionDiagram from './ConnectionDiagram';
import EntryDetail from './EntryDetail';
import ReaderView from './ReaderView';
import TrackedCompartment from './TrackedCompartment';
import VaultRail from './VaultRail';
import VaultSummary from './VaultSummary';
import { confirmChirpSx, unsealSx } from './vaultMotion';

/**
 * The Data Vault — a named, sealed library of WH systems the corp has
 * scouted: label, notes and a compartment tag per saved system, the
 * connections logged between them, and the J->J hops the location tracker
 * observed on its own.
 *
 * A reference store, not a live route tracker and not a rebuilt Pathfinder.
 * No positions, no dragging: compartments are the primary view, and the
 * optional diagram is computed fresh every render with nothing saved.
 */
const DataVault: React.FC = () => {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [selectedVaultId, setSelectedVaultId] = useState<number | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<MapSystem | null>(null);
  const [showDiagram, setShowDiagram] = useState(false);
  const [promotingKey, setPromotingKey] = useState<string | null>(null);
  // The compartment the next deposit lands in — sticky, because filing is
  // bursty: you scout a chain and everything from that run belongs together.
  const [depositTag, setDepositTag] = useState('');
  const [query, setQuery] = useState('');
  const [deletingVaultId, setDeletingVaultId] = useState<number | null>(null);
  // Reading is the default for everyone, including people who can edit: you
  // arrive at a vault to find something out far more often than to change it.
  const [mode, setMode] = useState<'read' | 'edit'>('read');

  const { data: vaults, isLoading } = useQuery({ queryKey: ['maps'], queryFn: fetchMaps });
  const activeVaultId = selectedVaultId ?? vaults?.[0]?.id ?? null;
  const activeVault = (vaults ?? []).find((v) => v.id === activeVaultId) ?? null;

  const { data: favorites } = useQuery({ queryKey: ['systems', 'favorites'], queryFn: fetchFavoriteSystems });
  const favoriteIds = useMemo(
    () => new Set((favorites?.personal ?? []).map((f) => f.solar_system_id)),
    [favorites],
  );

  const { data: systems } = useQuery({
    queryKey: ['map-systems', activeVaultId],
    queryFn: () => fetchMapSystems(activeVaultId!),
    enabled: !!activeVaultId,
  });
  const { data: connections } = useQuery({
    queryKey: ['map-connections', activeVaultId],
    queryFn: () => fetchMapConnections(activeVaultId!),
    enabled: !!activeVaultId,
  });
  // Same query key TrackedCompartment uses, so the two share one cache entry
  // rather than both fetching. Enabled whenever a diagram is actually on
  // screen: the reader draws one unconditionally, while the editor hides its
  // behind a toggle. Gating on `showDiagram` alone left the reader — the
  // default mode for everyone — fetching nothing, so the self-drawing half
  // of the diagram was invisible exactly where it matters most.
  const trackedForDiagram = useQuery({
    queryKey: ['map-tracked', activeVaultId],
    queryFn: () => fetchTrackedConnections(activeVaultId!),
    enabled: !!activeVaultId && (showDiagram || mode === 'read'),
  });

  const createVaultMutation = useMutation({
    mutationFn: (v: { name: string; scope: ShareScope; groupId: number | null }) =>
      createMap({ name: v.name, shareScope: v.scope, shareGroupId: v.groupId ?? undefined }),
    onSuccess: (folder) => {
      setSelectedVaultId(folder.id);
      setSelectedSystem(null);
      queryClient.invalidateQueries({ queryKey: ['maps'] });
    },
  });

  // Owner-only server-side (see requireMapOwner in routes/maps.ts) — cascades
  // every entry and connection in the vault, so this is the one delete in
  // the whole page that asks first rather than acting instantly.
  const deleteVaultMutation = useMutation({
    mutationFn: (id: number) => deleteMap(id),
    onSuccess: (_res, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['maps'] });
      // The vault took its pinned systems with it, so the dashboard's
      // Favorites section is now showing entries that no longer exist.
      queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] });
      if (activeVaultId === deletedId) {
        setSelectedVaultId(null);
        setSelectedSystem(null);
      }
    },
  });

  /**
   * Adding or removing an entry changes three things a user can see at once,
   * and only the first was being refreshed: this vault's entry list, the
   * per-vault `entry_count` on the rail and the dashboard's vault panel
   * (['maps']), and the dashboard's Favorites section when the entry was
   * pinned (['systems','favorites']). The counts and pins kept showing the
   * pre-change numbers until something else happened to refetch them, so the
   * UI contradicted the action the user had just taken.
   */
  const invalidateSystems = () => {
    queryClient.invalidateQueries({ queryKey: ['map-systems', activeVaultId] });
    queryClient.invalidateQueries({ queryKey: ['maps'] });
    queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] });
  };

  const addSystemMutation = useMutation({
    mutationFn: (s: SystemSearchResult) =>
      addMapSystem(activeVaultId!, { solarSystemId: s.solar_system_id, tag: depositTag || undefined }),
    onSuccess: invalidateSystems,
  });
  const renameCompartmentMutation = useMutation({
    mutationFn: (v: { current: string; next: string }) => renameCompartment(activeVaultId!, v.current, v.next),
    onSuccess: invalidateSystems,
  });
  const removeSystemMutation = useMutation({
    mutationFn: (systemId: number) => removeMapSystem(activeVaultId!, systemId),
    onSuccess: () => {
      invalidateSystems();
      setSelectedSystem(null);
    },
  });
  const pinMutation = useMutation({
    mutationFn: (s: MapSystem) => updateMapSystem(activeVaultId!, s.id, { isPinned: !s.is_pinned }),
    onSuccess: () => {
      invalidateSystems();
      queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] });
    },
  });
  const tagMutation = useMutation({
    mutationFn: (v: { system: MapSystem; tag: string }) => updateMapSystem(activeVaultId!, v.system.id, { tag: v.tag }),
    onSuccess: invalidateSystems,
  });
  /** Promotes an auto-tracked entry to a curated one. Also drops the 'auto'
   * compartment: a kept entry belongs wherever its keeper files it, not in
   * the drawer the poller uses for churn. */
  const keepMutation = useMutation({
    mutationFn: (s: MapSystem) => updateMapSystem(activeVaultId!, s.id, { keep: true, tag: '' }),
    onSuccess: invalidateSystems,
  });
  const favoriteMutation = useMutation({
    mutationFn: (s: MapSystem) =>
      favoriteIds.has(s.solar_system_id) ? removeFavoriteSystem(s.solar_system_id) : addFavoriteSystem(s.solar_system_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] }),
  });

  /** Promotion maps the tracked pair back onto this vault's own rows, then
   * writes an ordinary curated connection — the poller never touches
   * map_connections itself. */
  const promoteMutation = useMutation({
    mutationFn: (t: TrackedConnection) => {
      const from = (systems ?? []).find((s) => s.solar_system_id === t.from_system_id);
      const to = (systems ?? []).find((s) => s.solar_system_id === t.to_system_id);
      if (!from || !to) throw new Error('both systems must be saved to this vault first');
      return addMapConnection(activeVaultId!, {
        fromMapSystemId: from.id,
        toMapSystemId: to.id,
        massStatus: 'unknown',
        lifeStatus: 'unknown',
        notes: 'Observed by tracker',
      });
    },
    onSettled: () => setPromotingKey(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['map-connections', activeVaultId] }),
  });

  const handlers: CompartmentHandlers = {
    onSelect: (s) => setSelectedSystem((prev) => (prev?.id === s.id ? null : s)),
    onRemove: (id) => removeSystemMutation.mutate(id),
    onTogglePin: (s) => pinMutation.mutate(s),
    onToggleFavorite: (s) => favoriteMutation.mutate(s),
    onSetTag: (system, tag) => {
      setDepositTag(tag);
      tagMutation.mutate({ system, tag });
    },
    onKeep: (system) => keepMutation.mutate(system),
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: C.wormhole }} />
      </Box>
    );
  }

  // A grant can be revoked while the page is open; edit mode must not
  // survive that. The API would refuse the writes anyway — this keeps the UI
  // from offering them.
  const effectiveMode = me.canEdit ? mode : 'read';
  const rows = systems ?? [];
  const knownTags = [...new Set(rows.map((s) => s.tag?.trim()).filter((t): t is string => !!t))].sort();
  // One free-text filter over everything a scout would search by — name,
  // nickname, compartment, class, and the note itself. A structured filter
  // per field would be four controls for what one box answers.
  const needle = query.trim().toLowerCase();
  const visibleRows = needle
    ? rows.filter((s) =>
        [s.name, s.label, s.tag, s.system_class, s.notes].some((v) => v?.toLowerCase().includes(needle)),
      )
    : rows;

  return (
    <Stack spacing={2.5} sx={revealSx()}>
      {/* A single wrapping row, not a dedicated column — see VaultRail's own
          note. A corp's handful of vaults cost exactly the height they need
          this way, and the reclaimed width goes straight to whatever is
          actually open below. */}
      <VaultRail
        vaults={vaults ?? []}
        activeId={activeVaultId}
        ownCharacterId={me.character?.id}
        isAdmin={me.isAdmin}
        canEdit={me.canEdit}
        onSelect={(id) => {
          setSelectedVaultId(id);
          setSelectedSystem(null);
        }}
        onCreate={(v) => createVaultMutation.mutate(v)}
        onDelete={(id) => setDeletingVaultId(id)}
        creating={createVaultMutation.isPending}
        createError={createVaultMutation.isError}
      />

      <AskDialog
        open={deletingVaultId !== null}
        title={`Delete "${(vaults ?? []).find((v) => v.id === deletingVaultId)?.name ?? 'this vault'}"?`}
        body="Every entry and logged connection in it goes with it. This cannot be undone."
        confirmLabel="Delete vault"
        destructive
        onCancel={() => setDeletingVaultId(null)}
        onConfirm={() => {
          if (deletingVaultId !== null) deleteVaultMutation.mutate(deletingVaultId);
          setDeletingVaultId(null);
        }}
      />

      {/* Reading and editing are two different jobs, and the page used to
          only do the second one. The switch is here rather than per-panel so
          the whole vault changes mode at once — a page that is half briefing
          and half form is the state this split exists to end. Hidden
          entirely for a read-only grant: offering a mode the API refuses is
          worse than not offering it. */}
      {activeVault && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {me.canEdit ? (
            <>
              {(['read', 'edit'] as const).map((m) => (
                <Box key={m} component="button" type="button" onClick={() => setMode(m)} sx={modeSx(mode === m)}>
                  {m === 'read' ? 'Read' : 'Edit'}
                </Box>
              ))}
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.textDim }}>
                {effectiveMode === 'read' ? 'Everything this vault holds.' : 'Deposit systems, write notes, log connections.'}
              </Typography>
            </>
          ) : (
            <>
              <StatusBadge label="read-only" accent={C.gold} dim icon={<LockIcon sx={{ fontSize: 11, color: C.gold }} />} />
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.textDim }}>
                Your access is read-only. An admin can grant editing in the Admin panel.
              </Typography>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Filtering used to live inside the editor toolbar, which meant a
              reader — the person most likely to be looking something up in a
              forty-system vault — had no way to search it. It sits with the
              mode switch now and narrows both views. */}
          <TextField
            size="small"
            placeholder="Filter systems&hellip;"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 220 }, ...frameSx(C.cyan), borderRadius: '10px' }}
          />
        </Stack>
      )}

      {!activeVaultId || !activeVault ? (
        <EmptyVaultState />
      ) : effectiveMode === 'read' ? (
        <ReaderView
          vault={activeVault}
          systems={visibleRows}
          allSystems={rows}
          connections={connections ?? []}
          tracked={trackedForDiagram.data}
          query={query}
        />
      ) : (
        // One full-width column, not a list + 340px rail. The editor used to
        // live in that rail, which meant a form squeezed into 340px while the
        // list next to it had 1100px of empty space to the right — and the
        // system you clicked was on the opposite side of the screen from the
        // fields editing it. It now opens inline beneath its own row.
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 2.5 }}
            >
              <SystemAutocomplete onSelect={(s) => addSystemMutation.mutate(s)} />
              <DepositCompartment value={depositTag} knownTags={knownTags} onChange={setDepositTag} />
              {addSystemMutation.isSuccess && (
                <Box sx={confirmChirpSx}>
                  <StatusBadge label="Deposited" accent={C.cyan} />
                </Box>
              )}
              {addSystemMutation.isError && (
                <Typography sx={{ color: C.red, fontSize: '0.75rem' }}>Couldn&rsquo;t add system.</Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <FormControlLabel
                control={<Switch size="small" checked={showDiagram} onChange={(e) => setShowDiagram(e.target.checked)} />}
                label={
                  <Typography sx={{ fontSize: '0.78rem', color: C.textDim, fontFamily: FONT.mono }}>Diagram</Typography>
                }
              />
            </Stack>

            {/* The diagram is the "where am I in the chain" overview — it
                belongs above the compartment list it summarizes, not buried
                below it, so turning it on doesn't ask for a scroll past
                everything it's meant to orient you before reading. */}
            {showDiagram && rows.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <ConnectionDiagram systems={rows} connections={connections ?? []} tracked={trackedForDiagram.data} />
              </Box>
            )}

            {rows.length === 0 ? (
              <EmptyCompartments />
            ) : visibleRows.length === 0 ? (
              <ScanFrame color={C.cyan} corners={false} sx={unsealSx()}>
                <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.84rem' }}>
                  Nothing in this vault matches &ldquo;{query}&rdquo;.
                </Typography>
              </ScanFrame>
            ) : (
              <CompartmentList
                systems={visibleRows}
                selectedId={selectedSystem?.id ?? null}
                favoriteIds={favoriteIds}
                onRenameCompartment={(current, next) => renameCompartmentMutation.mutate({ current, next })}
                handlers={handlers}
                // The editor renders here, inline under the row it belongs to,
                // so the fields sit where the click landed and get the full
                // page width to lay out in.
                renderDetail={(s) => (
                  <EntryDetail mapId={activeVaultId} system={s} systems={rows} onSaved={setSelectedSystem} />
                )}
              />
            )}

            <TrackedCompartment
              mapId={activeVaultId}
              promotingKey={promotingKey}
              onPromote={(t) => {
                setPromotingKey(`${t.from_system_id}-${t.to_system_id}`);
                promoteMutation.mutate(t);
              }}
            />
          </Box>

          {/* The vault's own stats and connections, below the list it
              describes rather than in a rail beside it. */}
          <Box sx={{ mt: 2.5 }}>
            <VaultSummary
              vault={activeVault}
              systems={rows}
              connections={connections ?? []}
              canDelete={activeVault.owner_character_id === me.character?.id || me.isAdmin}
              isOwner={activeVault.owner_character_id === me.character?.id}
              onDeleteVault={() => deleteVaultMutation.mutate(activeVault.id)}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

/** First run: orient before performing. An empty vault should say what a
 * vault is for and what the next action is, not just report emptiness. */
const EmptyVaultState: React.FC = () => (
  <ScanFrame color={C.wormhole} sx={unsealSx()}>
    <Stack spacing={1.25} sx={{ py: 3, alignItems: 'flex-start' }}>
      <Typography sx={{ fontFamily: FONT.display, fontWeight: 700, fontSize: '1.1rem', color: C.textBright }}>
        No vault open
      </Typography>
      <Typography sx={{ color: C.textDim, fontSize: '0.86rem', maxWidth: 460 }}>
        A vault is a sealed, shareable library of systems — home holes, staging, farms — with notes,
        connections, and the jumps your characters make tracked automatically.
      </Typography>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.75rem', color: C.cyan }}>
        Create one above to get started.
      </Typography>
    </Stack>
  </ScanFrame>
);

const EmptyCompartments: React.FC = () => (
  <ScanFrame color={C.cyan} corners={false} sx={unsealSx()}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Chevron color={C.cyan} pulse />
      <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.84rem' }}>
        Vault is empty — search above to deposit the first system
      </Typography>
    </Stack>
  </ScanFrame>
);

/** The read/edit switch. Deliberately the same segmented shape the entry
 * editor uses for its own two-way choices, rather than a third control
 * vocabulary for the page's most important toggle. */
const modeSx = (active: boolean) =>
  ({
    fontFamily: FONT.sub,
    fontSize: '0.74rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    px: 1.75,
    py: 0.5,
    borderRadius: '999px',
    cursor: 'pointer',
    background: active ? hexToRgba(C.cyan, 0.1) : 'transparent',
    border: `1px solid ${active ? hexToRgba(C.cyan, 0.5) : C.hairline}`,
    color: active ? C.cyan : C.textDim,
    transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': { color: C.cyan },
  }) as const;

/** Which drawer the next deposit goes into. Freeform (a new compartment is
 * just a name nobody used yet), but the existing ones are one click away so
 * the common case never involves typing — or misspelling — an existing tag. */
const DepositCompartment: React.FC<{ value: string; knownTags: string[]; onChange: (t: string) => void }> = ({
  value,
  knownTags,
  onChange,
}) => (
  <Autocomplete
    freeSolo
    size="small"
    options={knownTags}
    value={value}
    onInputChange={(_, v) => onChange(v)}
    sx={{ width: { xs: '100%', sm: 210 }, ...frameSx(C.wormhole), borderRadius: '10px' }}
    renderInput={(params) => <TextField {...params} placeholder="into group&hellip;" />}
  />
);

const SystemAutocomplete: React.FC<{ onSelect: (s: SystemSearchResult) => void }> = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const { data } = useQuery({ queryKey: ['system-search', q], queryFn: () => searchSystems(q), enabled: q.length >= 2 });

  return (
    <Autocomplete
      size="small"
      options={data ?? []}
      getOptionLabel={(o) => o.name}
      onInputChange={(_, v) => setQ(v)}
      onChange={(_, v) => v && onSelect(v)}
      sx={{ width: { xs: '100%', sm: 260 }, ...frameSx(C.cyan), borderRadius: '10px' }}
      renderInput={(params) => <TextField {...params} placeholder="Deposit a system&hellip;" />}
    />
  );
};

export default DataVault;
